/// <reference types="vite/client" />
import React from 'react'
import ReactDOM from 'react-dom/client'
import { Toaster } from 'sonner'
import App from './App'
import { logger } from '@/utils/logger'

import './global.css'
import './x-men-theme.css'

// Global safety net for async failures the React error boundary cannot catch
// (rejected fire-and-forget promises, errors thrown outside the render phase).
// Without this, such failures are invisible in production with no telemetry.
window.addEventListener('unhandledrejection', (event) => {
  const reason: unknown = event.reason;
  logger.error('Unhandled promise rejection', {
    reason: reason instanceof Error ? reason.message : String(reason),
  });
});
window.addEventListener('error', (event) => {
  logger.error('Uncaught error', {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
  });
});

// Unregister any lingering service worker from a previous install (the
// pre-Feb-2025 offline SW was removed from source but may still be active
// on devices that installed the PWA back then, serving stale JS). This
// runs on every boot so even a cached install eventually drops the SW.
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations()
    .then((regs) => {
      for (const reg of regs) {
        reg.unregister();
      }
    })
    .catch(() => { /* ignore */ });
}

// Idempotent mount: guard against double-invocation (e.g. both the
// DOMContentLoaded listener and a synchronous call, or HMR re-runs)
// creating two React roots on the same element.
const mount = (): void => {
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    return;
  }
  const guarded = rootElement as HTMLElement & { __mounted?: boolean };
  if (guarded.__mounted) return;
  guarded.__mounted = true;
  ReactDOM.createRoot(rootElement).render(
    <>
      <App />
      <Toaster position="top-center" richColors />
    </>
  );
};

if (document.readyState === 'loading') {
  window.addEventListener('DOMContentLoaded', mount, { once: true });
} else {
  mount();
}
